import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { EventFormValues } from "../components/events/EventForm";
import { supabase } from "../supabase/supabase-client";
import { LogeEvent, Participant } from "../types/entities";

export enum VoteAnswer {
  YES = 1,
  MAYBE = 0.5,
  NO = 0,
}

export const useListAllEvents = () => {
  const getAllEvents = async () => {
    const { data, error } = await supabase
      .from("loge_events")
      .select("*")
      .eq("deleted", false)
      .gte("date", dayjs().startOf("day").toISOString());
    if (error) throw error;
    return data;
  };

  return useQuery({
    queryKey: ["events"],
    queryFn: getAllEvents,
    select: (data) => {
      return data.sort((a, b) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });
    },
  });
};

/**
 * Every samling the loge has ever held, newest first — including the ones
 * that are already over.
 *
 * `useListAllEvents` deliberately cuts everything before today: the calendar
 * is about what's coming. The ledger is the opposite — a booking is nearly
 * always attributed to an evening that has already happened and been paid
 * for, so that hook can't be reused for the picker.
 */
export const useEventHistory = () => {
  return useQuery({
    queryKey: ["events", "history"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("loge_events")
        .select("*")
        .eq("deleted", false)
        .order("date", { ascending: false });
      if (error) throw error;
      return data as LogeEvent[];
    },
  });
};

export const useGetEventParticipants = (eventId: number) => {
  const getParticipants = async () => {
    const { data, error } = await supabase
      .from("loge_event_participants")
      .select("*")
      .eq("loge_event_id", eventId);
    if (error) throw error;
    return data as Participant[];
  };

  return useQuery({
    queryKey: ["event_participants", eventId],
    queryFn: getParticipants,
  });
};

export const useGetEvent = (eventId?: string) => {
  const getEvent = async () => {
    if (!!eventId) {
      const { data, error } = await supabase
        .from("loge_events")
        .select("*")
        .eq("id", eventId);
      if (error) throw error;
      return data[0];
    }
    return undefined;
  };

  const EVENT_QUERY_KEY = ["event", eventId];
  return useQuery({
    queryKey: EVENT_QUERY_KEY,
    queryFn: getEvent,
    enabled: !!eventId,
  });
};

interface UpdateEventPayload {
  eventId: LogeEvent["id"];
  formValues: EventFormValues;
}

// EventForm collects the event's date and time-of-day as separate inputs
// (matching the "Redigér samling" design); combine them into one timestamp
// for the `date` column, which is what the rest of the app reads.
const combineDateAndTime = (date: string, time: string) =>
  new Date(`${date}T${time || "00:00"}`).toISOString();

export const useUpdateEvent = () => {
  return useMutation(async (payload: UpdateEventPayload) => {
    const { eventId, formValues } = payload;
    const { error } = await supabase
      .from("loge_events")
      .update({
        date: combineDateAndTime(formValues.date, formValues.time),
        emoji: formValues.emoji,
        host: formValues.host,
        status: formValues.status,
        description: formValues.description,
        title: formValues.title,
        show_vote_link: formValues.showVoteLink,
      })
      .eq("id", eventId)
      .select();
    if (error) throw error;
  });
};

export const useAddEvent = () => {
  return useMutation(async (formValues: EventFormValues) => {
    const { data, error } = await supabase
      .from("loge_events")
      .insert([
        {
          date: combineDateAndTime(formValues.date, formValues.time),
          emoji: formValues.emoji,
          title: formValues.title,
          status: formValues.status,
          description: formValues.description,
          host: formValues.host,
          show_vote_link: formValues.showVoteLink,
        },
      ])
      .select()
      .single();
    if (error) throw error;
    return data;
  });
};

export interface CloseEventWithWinnerPayload {
  eventId: LogeEvent["id"];
  winningDate: string;
}

// "Luk og vælg vinder" — closes voting by confirming the event on the
// winning date option's date.
export const useCloseEventWithWinner = () => {
  return useMutation(async (payload: CloseEventWithWinnerPayload) => {
    const { error } = await supabase
      .from("loge_events")
      .update({ status: "confirmed", date: payload.winningDate })
      .eq("id", payload.eventId);
    if (error) throw error;
  });
};

// "Genåbn afstemning" — back to draft so dates/votes can be edited again.
export const useReopenEvent = () => {
  return useMutation(async (eventId: LogeEvent["id"]) => {
    const { error } = await supabase
      .from("loge_events")
      .update({ status: "draft" })
      .eq("id", eventId);
    if (error) throw error;
  });
};

export interface SetShowVoteLinkPayload {
  eventId: LogeEvent["id"];
  showVoteLink: boolean;
}

// Whether the "Stem på datoer" CTA appears on the samling for anyone but
// its host/admin — a single-field sibling of useUpdateEvent, which needs a
// whole EventFormValues just to flip this one thing. EditVotableEventPage
// is the one place a host who isn't an admin can reach this toggle at all.
export const useSetShowVoteLink = () => {
  return useMutation(async (payload: SetShowVoteLinkPayload) => {
    const { error } = await supabase
      .from("loge_events")
      .update({ show_vote_link: payload.showVoteLink })
      .eq("id", payload.eventId);
    if (error) throw error;
  });
};

export const useDeleteEvent = () => {
  return useMutation(async (eventId: number) => {
    const { error } = await supabase
      .from("loge_events")
      .update({ deleted: true })
      .eq("id", eventId);
    if (error) throw error;
  });
};
